import React from 'react';
import moment from 'moment';
import { Info } from 'lucide-react';

import { dateFormat } from '../../../../helper';
import { useProgramForm } from '../../../../context/ProgramFormContext';
import SectionCard from '../ui/SectionCard';
import Field from '../ui/Field';
import { RangeInput, TimeInput } from '../ui/Inputs';
import { gray, ink, radius } from '../theme';

/**
 * Section 4 — Program Dates and Time. Ports `AddProgramModal.js:2395-2453`.
 *
 * The design promotes this above Location; the old form had it sixth. Nothing depends on
 * the order.
 *
 * With `byDates === 1` the date range disappears: start and end are derived from the
 * min/max of the program date blocks at submit time. A callout takes its place saying so,
 * since otherwise the section named for the program dates shows none.
 */
const Timing = () => {
    const {
        userInput,
        byDates,
        handleDateRangeChange,
        handleTimeChange,
        fieldStatus,
        requiredOnly,
    } = useProgramForm();

    return (
        <SectionCard
            id="schedule"
            step={4}
            title="Program Dates and Time"
            description="When the program runs and the daily check in / check out"
        >
            <div className="row">
                {byDates !== 1 ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="Start Date / End Date"
                        htmlFor="startDate"
                        required
                        hint={
                            userInput.registerCloseDate === ''
                                ? 'Set the standard registration dates first.'
                                : ''
                        }
                        {...fieldStatus('endDate')}
                    >
                        <RangeInput
                            id="startDate"
                            error={fieldStatus('endDate').error}
                            disabled={userInput.registerCloseDate === ''}
                            disabledDate={d =>
                                userInput.dateEarlyRegisterOpen
                                    ? d.isSameOrBefore(dateFormat(userInput.dateEarlyRegisterOpen))
                                    : d.isSameOrBefore(dateFormat(userInput.registerOpenDate))
                            }
                            value={
                                userInput.startDateFormatted
                                    ? [
                                        moment.utc(userInput.startDateFormatted),
                                        moment.utc(userInput.endDateFormatted),
                                    ]
                                    : ''
                            }
                            change={e => handleDateRangeChange(e, 'startDate', 'endDate')}
                        />
                    </Field>
                ) : (
                    /* The range does not just become optional here, it moves: each Price date
                       block carries its own. Saying so is the whole point of this callout —
                       without it the field simply vanishes from the section named after it,
                       which reads as a bug. Ported from the prototype's `ScheduleSection.tsx`.

                       Styled inline rather than through `theme.js`, which reserves itself for
                       what more than one component uses. The tokens are the prototype's
                       Tailwind one for one: `rounded-xl`, `border-gray-200`, `bg-gray-50`,
                       `text-ink-400` on the icon, `text-[12.5px] text-ink-500` on the copy. */
                    <div className="col-12 mb-3">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                borderRadius: radius.lg,
                                border: `1px solid ${gray[200]}`,
                                backgroundColor: gray[50],
                                padding: '12px 16px',
                            }}
                        >
                            <Info
                                size={16}
                                color={ink[400]}
                                style={{ marginTop: '2px', flexShrink: 0 }}
                                aria-hidden="true"
                            />
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: '12.5px',
                                    lineHeight: 1.6,
                                    color: ink[500],
                                }}
                            >
                                Program dates are set per date block in the <strong>Price</strong>{' '}
                                section because pricing is based on Program Dates.
                            </p>
                        </div>
                    </div>
                )}

                <Field
                    className="col-md-3 mb-3"
                    label="Check-In Time"
                    htmlFor="checkInTime"
                    required
                    {...fieldStatus('checkInTime')}
                >
                    <TimeInput
                        id="checkInTime"
                        minuteStep={15}
                        use12Hours
                        format="h:mm a"
                        value={userInput.checkInTime ? moment(userInput.checkInTime, 'h:mm a') : ''}
                        change={e => handleTimeChange(e, 'checkInTime')}
                        error={fieldStatus('checkInTime').error}
                    />
                </Field>

                <Field
                    className="col-md-3 mb-3"
                    label="Check-Out Time"
                    htmlFor="checkOutTime"
                    required
                    hint={userInput.checkInTime ? '' : 'Set the check-in time first.'}
                    {...fieldStatus('checkOutTime')}
                >
                    <TimeInput
                        id="checkOutTime"
                        minuteStep={15}
                        use12Hours
                        format="h:mm a"
                        disabled={!userInput.checkInTime}
                        value={userInput.checkOutTime ? moment(userInput.checkOutTime, 'h:mm a') : ''}
                        change={e => handleTimeChange(e, 'checkOutTime')}
                        error={fieldStatus('checkOutTime').error}
                    />
                </Field>

                {!requiredOnly ? (
                    <Field
                        className="col-md-4 mb-3"
                        label="Alternate Last Day Check-Out Time"
                        htmlFor="attendanceHour"
                        hint="Used when campers leave earlier on the final day."
                    >
                        <TimeInput
                            id="attendanceHour"
                            minuteStep={15}
                            use12Hours
                            format="h:mm a"
                            value={userInput.attendanceHour ? moment(userInput.attendanceHour, 'h:mm a') : ''}
                            change={e => handleTimeChange(e, 'attendanceHour')}
                        />
                    </Field>
                ) : ''}
            </div>
        </SectionCard>
    );
};

export default Timing;
